/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useRef, useState } from "react";

import * as React from "react";
import styled, { css, SimpleInterpolation } from "styled-components";
import { devices, fontFamilies, useIsMobile } from "components/shared/Styles";
import { WithDottedBorder } from "components/shared/DottedBorder";
import useDropWaveformBlockOrNativeFile, {
  makeMultiFileDropHandler,
} from "hooks/useDropWaveformBlockOrNativeFile";
import { useEnhanceSpeech } from "components/EnhancedSpeech/useEnhanceSpeech";
import addFilesIcon from "images/EnhanceSpeech/add-audio-illustration.svg";
import { TrackDetail } from "components/EnhancedSpeech/ProTierEnhanceSpeech/TrackDetail";
import { TrackList } from "components/EnhancedSpeech/ProTierEnhanceSpeech/TrackList";
import { EmptyState } from "components/EnhancedSpeech/ProTierEnhanceSpeech/EmptyState";
import { webComponentShim } from "components/shared/WebComponentShim";
import { ConditionalWrapper } from "components/shared/ConditionalWrapper";
import { useSelectableTrackItems } from "components/EnhancedSpeech/ProTierEnhanceSpeech/useSelectableTrackItems";
import { addToast } from "actions/ToastMessageActions";
import { PremiumIcon } from "components/shared/PremiumIcon";
import {
  ACCEPTED_CONTENT_TYPES,
  ACCEPTED_FILE_TYPES,
} from "constants/AcceptedFiles";
import { ModelVersionPicker } from "components/EnhancedSpeech/ModelVersionPicker";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";
import { useCurrentUser } from "actions/CurrentUserActions";
import {
  InfoContainer,
  InfoIcon,
} from "components/EnhancedSpeech/ProTierEnhanceSpeech/InfoContainer";

const Heading = styled.h1`
  font: normal normal 900 36px/45px ${fontFamilies};
  color: var(--spectrum-global-color-gray-900);
`;

const Title = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  grid-area: page-title;
  align-self: baseline;
  gap: 12px;

  ${devices.small} {
    justify-content: center;
  }
`;

export const Dropzone = styled.div<{ active: boolean }>`
  grid-area: drop-zone;
  border-radius: 18px;
  width: 100%;
  position: relative;

  ${({ active }): SimpleInterpolation => {
    if (active) {
      return css`
        background-color: rgba(92, 92, 224, 0.16);
      `;
    }
  }})
`;

const DropzoneContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 12px;
  padding: 24px 12px;
`;

const FloatingOptions = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const OptionsGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

const FilePickerButton = styled(webComponentShim("sp-button"))`
  --spectrum-button-m-secondary-outline-texticon-text-color: var(
    --spectrum-global-color-gray-900
  );
`;

// grid doesn't support variable gap sizes, so the different vertical gaps are
// represented as rows using `.` to signify them in the template
const ProEnhanceSpeechContainer = styled.div<{ hasEnhancements: boolean }>`
  max-width: 1400px;
  width: 100%;
  max-height: 100%;
  overflow: hidden;
  height: 100%;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
  padding: 0 64px;
  align-content: start;
  justify-content: space-between;
  display: grid;
  grid-template-columns: minmax(0, 430px) minmax(377px, 1fr);
  column-gap: 64px;
  grid-template-rows:
    auto
    24px
    minmax(220px, max-content)
    20px
    auto;
  grid-template-areas:
    "page-title track-header"
    ".          ."
    "drop-zone  track-detail"
    ".          track-detail"
    "track-list track-detail";
  ${({ hasEnhancements }): SimpleInterpolation => {
    if (!hasEnhancements) {
      return css`
        grid-template-areas:
          "page-title empty-state"
          ".          empty-state"
          "drop-zone  empty-state"
          ".          empty-state"
          "empty-list empty-state";
      `;
    }
  }}

  ${devices.medium} {
    padding: 0px 24px 0;
    column-gap: 24px;
  }

  ${devices.small} {
    column-gap: 0;
    max-height: 100%;
    min-height: fit-content;
    height: 100%;
    padding: 0;
    grid-template-columns: 100%;
    grid-template-rows: auto auto auto;
    grid-template-areas:
      "page-title"
      "drop-zone"
      "track-list";

    ${({ hasEnhancements }): SimpleInterpolation => {
      if (!hasEnhancements) {
        return css`
          grid-template-rows: auto auto;
          grid-template-areas:
            "page-title"
            "drop-zone";
        `;
      }
    }}
  }
`;

const SupportedFilesHeader = styled.div`
  font-weight: bold;
`;

const SupportedFilesAudio = styled.div`
  margin-top: 10px;
`;

const SupportedFilesVideo = styled.div`
  margin-top: 10px;
`;

const DropZoneIcon = styled.img.attrs(() => ({
  src: addFilesIcon,
}))`
  width: 88px;
  height: 75px;
`;

export const ProTierEnhanceSpeech: React.FC = () => {
  const enhanceSpeech = useEnhanceSpeech();

  const { trackItems, trackDetail } = useSelectableTrackItems(
    enhanceSpeech.enhancements
  );

  const { currentUser } = useCurrentUser();
  const enhanceSpeechV2PremiumEnabled = Boolean(
    currentUser?.enhanceSpeechV2PremiumEnabled
  );

  const [modelVersion, setModelVersion] = useState<ModelVersion>(
    enhanceSpeechV2PremiumEnabled ? ModelVersion.v2 : ModelVersion.v1
  );

  const { isDraggingOver, dropRef } = useDropWaveformBlockOrNativeFile({
    acceptedContentTypes: ACCEPTED_CONTENT_TYPES,
    onFileDroppedHandler: makeMultiFileDropHandler((droppedFiles: File[]) => {
      for (const droppedFile of droppedFiles) {
        enhanceSpeech.processFile(droppedFile, modelVersion).catch((reason) => {
          console.log(
            `Failed to process dropped file ${droppedFile.name}: ${reason}`
          );
          addToast({
            message: I18n.t("enhance_speech.premium.enqueue_failed", {
              file_name: droppedFile.name,
            }),
          });
        });
      }
    }),
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onFileChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFiles = fileInputRef.current?.files;

    if (!selectedFiles) {
      return;
    }

    for (const selectedFile of selectedFiles) {
      enhanceSpeech.processFile(selectedFile, modelVersion).catch((reason) => {
        console.log(
          `Failed to process selected file ${selectedFile.name}: ${reason}`
        );
        addToast({
          message: I18n.t("enhance_speech.premium.enqueue_failed", {
            file_name: selectedFile.name,
          }),
        });
      });
    }
    event.target.value = "";
  };

  const isMobile = useIsMobile();

  const hasEnhancements = enhanceSpeech.enhancements.length > 0;

  return (
    <ProEnhanceSpeechContainer hasEnhancements={hasEnhancements}>
      <>
        {/* Left column */}
        <Title>
          <Heading>{I18n.t("enhance_speech.premium.title")}</Heading>
          <PremiumIcon />
        </Title>
        <div style={{ gridArea: "drop-zone" }}>
          {/* TODO: Consider replacing with ResponsiveSwitch so that it works without requiring a re-render */}
          <ConditionalWrapper
            wrapperComponent={WithDottedBorder}
            wrapperProps={{ color: "rgb(92, 92, 224)", dashlength: 16 }}
            condition={!isMobile}
          >
            <Dropzone
              ref={dropRef}
              active={isDraggingOver}
              data-testid="enhance-speech-preview-drop-zone"
            >
              {!isMobile && (
                <FloatingOptions>
                  <OptionsGroup>
                    <InfoIcon>
                      <SupportedFilesHeader>
                        {I18n.t(
                          "enhance_speech.premium.filetypes_tooltip_header"
                        )}
                      </SupportedFilesHeader>
                      <SupportedFilesAudio>
                        {I18n.t(
                          "enhance_speech.premium.filetypes_tooltip_files_audio"
                        )}
                      </SupportedFilesAudio>
                      <SupportedFilesVideo>
                        {I18n.t(
                          "enhance_speech.premium.filetypes_tooltip_files_video"
                        )}
                      </SupportedFilesVideo>
                    </InfoIcon>
                    {enhanceSpeechV2PremiumEnabled && (
                      <ModelVersionPicker
                        modelVersion={modelVersion}
                        onSelected={setModelVersion}
                      />
                    )}
                  </OptionsGroup>
                </FloatingOptions>
              )}
              <DropzoneContent>
                <DropZoneIcon />
                <InfoContainer />
                {isMobile && (
                  <OptionsGroup>
                    <InfoIcon>
                      <SupportedFilesHeader>
                        {I18n.t(
                          "enhance_speech.premium.filetypes_tooltip_header"
                        )}
                      </SupportedFilesHeader>
                      <SupportedFilesAudio>
                        {I18n.t(
                          "enhance_speech.premium.filetypes_tooltip_files_audio"
                        )}
                      </SupportedFilesAudio>
                      <SupportedFilesVideo>
                        {I18n.t(
                          "enhance_speech.premium.filetypes_tooltip_files_video"
                        )}
                      </SupportedFilesVideo>
                    </InfoIcon>
                    {enhanceSpeechV2PremiumEnabled && (
                      <ModelVersionPicker
                        modelVersion={modelVersion}
                        onSelected={setModelVersion}
                      />
                    )}
                  </OptionsGroup>
                )}
                <FilePickerButton
                  size="l"
                  variant="secondary"
                  treatment="outline"
                  onClick={(): void => fileInputRef.current?.click()}
                >
                  {I18n.t("enhance_speech.choose_files")}
                  <input
                    type="file"
                    hidden
                    onChange={onFileChanged}
                    ref={fileInputRef}
                    id="enhance-file-upload"
                    accept={ACCEPTED_FILE_TYPES}
                    multiple={true}
                    aria-label="Choose files"
                  />
                </FilePickerButton>
              </DropzoneContent>
            </Dropzone>
          </ConditionalWrapper>
        </div>
        <TrackList trackItems={trackItems} />
      </>
      {trackDetail && <TrackDetail {...trackDetail} />}
      <EmptyState
        visible={!enhanceSpeech.isLoadingInitial && !hasEnhancements}
      />
    </ProEnhanceSpeechContainer>
  );
};
